/**
 * The survival HUD on the actual screen — health, food, exposure, ten on hand.
 *
 * 🔴 **This is the piece that had never been built.** 🎯 haugur, 2026-08-18:
 * *"what about the HUD, its not visiable on dev yet"* — he was right. Four
 * sample sheets existed and he had chosen one (option D, the scrim), but no
 * drawing code had ever run in the client.
 *
 * ⚠️ **Nothing about the appearance is decided here, and nothing may be.** The
 * marks come from `hud-parts.ts` and the layout from `hud-scene.ts`, which are
 * the same two files the sheets in `Design/samples/` are drawn from. This file
 * is only the plumbing: where the element sits, and when it is rebuilt.
 *
 * 🔴 **Rebuilt on CHANGE, never per frame.** The scene emits an SVG string, so a
 * redraw is a parse and a subtree swap — cheap at a few times a second,
 * ridiculous at sixty. Every setter here compares first and returns if nothing
 * moved.
 */

import {
  hudScale,
  makeScene,
  type AimTone,
  type ClusterState,
  type VariantName,
} from './hud-scene.ts';
import type { SlotContent } from './hud-parts.ts';
import {
  INVENTORY_ON_HAND,
  blockById,
  blockIdForKey,
  itemById,
  itemIdForKey,
  itemIconUri,
  durabilityOf,
  type Stack,
} from '@lofi/shared';
import { blockIconUri } from './blockicons.ts';

/**
 * ✅ **The variation haugur chose.** 🎯 *"i like picture 4 option D"* — the rod
 * over a gradient with no edges, so nothing has a boundary to be bulky.
 */
const VARIANT: VariantName = 'scrim';

/** Ten empty slots — what the on-hand row holds until the inventory is wired. */
const NOTHING_IN_HAND: readonly SlotContent[] = Array.from({ length: 10 }, () => ({}));

/**
 * What the bars read before the server has said anything.
 *
 * 🔴 **This used to be called `PLACEHOLDER` and it used to be the whole
 * truth** — health, food and exposure did not exist, so the bars stood at full
 * for the life of the session and an untouched bar looks exactly like a broken
 * one. ✅ **T129 wired health and food**, so those two are now only what is
 * drawn for the fraction of a second before the first `vitals` message lands,
 * and full is the right answer because a new player is neither hurt nor hungry.
 *
 * ✅ **`expo` STOPPED being a placeholder on 2026-08-21 (T131)** — it was the that
 * nothing moves: exposure is **T131**, which food had to exist before (**D64**
 * charges cold to the food bar). 0 is *comfortable*, which is truthful while no
 * weather can reach you.
 */
/**
 * What is drawn before the server has said anything.
 *
 * ⚠️ **Full and comfortable, which is what a new player IS.** A cluster that
 * started empty and filled a second later would look exactly like the fault
 * T129 set out to fix.
 *
 * ✅ **`expo` is no longer a placeholder value** — since T131 it is a real
 * reading the moment the first `vitals` message lands, and 0 here means
 * *nothing is biting yet*, not *we have no idea*.
 */
const BEFORE_THE_SERVER_SPEAKS = { health: 100, food: 100, expo: 0 } as const;

export class HudCluster {
  private readonly svg: SVGSVGElement;
  /** The cluster itself: bars, gauge, on-hand row. */
  private readonly body: SVGGElement;
  /** The controls hint, on its own so it can fade without redrawing the rest. */
  private readonly hint: SVGGElement;
  /** 🐑 The aim line — what is under the crosshair — on its own for the same reason. */
  private readonly aim: SVGGElement;
  private aimText: string | null = null;
  private aimTone: AimTone = 'plain';

  /** The cluster's size **in the units the drawing is written in** (T170). */
  private width = 0;
  private height = 0;
  /** What the element actually measures, kept only to notice when it changes. */
  private rawW = 0;
  private rawH = 0;
  /** 🔍 How much bigger than the design frame this screen draws — T170. */
  private scale = 1;
  private hintText: string | null = null;
  private state: ClusterState = {
    ...BEFORE_THE_SERVER_SPEAKS,
    reason: null,
    contents: NOTHING_IN_HAND,
    selected: 0,
  };

  constructor() {
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('class', 'hudcluster');
    /* ⚠️ Not focusable and not in the tab order: it is a picture of numbers. */
    this.svg.setAttribute('aria-hidden', 'true');

    this.body = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.hint = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.hint.setAttribute('class', 'hudcluster-hint');
    this.aim = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.svg.appendChild(this.body);
    this.svg.appendChild(this.hint);
    this.svg.appendChild(this.aim);
    document.body.appendChild(this.svg);

    /* The element sizes itself from CSS — `top: var(--topbar-h); bottom: 0` —
       and then tells the scene how big the screen is. That way nothing here
       needs to know the status bar's height, and the two cannot drift.
 
       🔴 **A ResizeObserver, NOT a window `resize` listener, and this was a
       real bug rather than a preference.** Measuring in the constructor reads
       the element before it has been laid out, when an `<svg>` still has its
       default intrinsic 300 × 150. The scene was duly built for a 150 px-tall
       screen and the browser then stretched that viewBox over the whole window
       — **the entire HUD at 4.27×**, notches the size of roof tiles. Nothing
       threw and no console message said a word; a `resize` listener never fired
       because the window never changed size. ⚠️ **It was found by looking at a
       screenshot and it could not have been found any other way.**
 
       The observer also covers the status bar changing height, which a window
       listener would have missed too. */
    new ResizeObserver(() => {
      this.measure();
    }).observe(this.svg);
    this.measure();
  }

  /** What the bars are reading. Values outside 0–100 are the drawing's problem, not ours. */
  setState(next: Partial<ClusterState>): void {
    const merged = { ...this.state, ...next };
    if (
      merged.health === this.state.health &&
      merged.food === this.state.food &&
      merged.expo === this.state.expo &&
      merged.reason === this.state.reason &&
      merged.selected === this.state.selected &&
      sameContents(merged.contents, this.state.contents)
    )
      return;
    this.state = merged;
    this.drawBody();
  }

  /**
   * 🍖 **The two bars, and why they are moving** (T129, protocol 12).
   *
   * 🔴 **This is the call that made the bars stop being a picture.** They were
   * drawn on 2026-08-18 and stood at 100 until this existed.
   *
   * ⚠️ **Rounded to whole percent on the way in, and that is not cosmetic.**
   * `setState` redraws whenever any value differs, and the underlying numbers
   * change by a millionth of a percent every frame — so without this the whole
   * cluster, ten slots and all, would be rebuilt sixty times a second forever.
   * The bars are 100 px of rod; nothing below a whole percent can be seen.
   *
   * `reason` is a finished sentence by the time it arrives here — the wire
   * carries a key and `main.ts` translates it, because the server does not know
   * what language this player reads (protocol 8's rule).
   */
  setVitals(
    healthPercent: number,
    foodPercent: number,
    reason: string | null,
    /** 🥶 −1 … 0, already snapped by `snapGauge` (T131, D84). */
    exposure = 0,
  ): void {
    this.setState({
      health: Math.round(healthPercent),
      food: Math.round(foodPercent),
      reason,
      expo: exposure,
    });
  }

  /**
   * 🎒 What the player is carrying, straight off the wire (protocol 9).
   *
   * Only the **on-hand** slots reach the screen here — the pack is slots 10–33
   * and is seen when the inventory is opened, which is the piece after this.
   *
   * ⚠️ **An item the HUD has no icon for still shows its count.** Today the four
   * items and the four icons match exactly, and `hud-icons.test.ts` fails the
   * build if a fifth item is added without one — but a slot that silently looks
   * empty while holding something is the kind of quiet wrongness this project
   * keeps paying for, so the number is drawn regardless.
   */
  setInventory(stacks: readonly Stack[], selected: number): void {
    this.stacks = stacks;
    this.selected = selected;
    this.drawRow();
  }

  /**
   * 🙌 **What is in the player's ARMS, or `null`** — T261, D123.
   *
   * 🎯 **haugur, having carried a sheep:** *"there should be a sheep block in on
   * hand inventory to show you that you have one."*
   *
   * 🔴 **IT IS NOT AN ITEM AND IT IS NOT IN `stacks`.** A carried sheep is a
   * creature the server keeps, with an id and a mind; it never enters the pack,
   * because an item stack could be dragged into a chest and **a sheep you could
   * store is a sheep you could stack and duplicate.**
   *
   * ⚠️ **It covers the SELECTED slot rather than taking one**, because that is
   * what is true: **your hands are full.** You cannot swing or place while
   * carrying, so the slot underneath is not usable anyway — and a row of ten
   * that sometimes had eleven things in it would be a worse lie.
   */
  setCarrying(content: SlotContent | null): void {
    /* ⚠️ Compared by the item key rather than by identity: this is called from
       the same message every beat and a fresh object each time would redraw the
       row for ever. */
    if ((this.carried?.item ?? null) === (content?.item ?? null)) return;
    this.carried = content;
    this.drawRow();
  }

  /** Build the ten on-hand slots and hand them to the scene. */
  private drawRow(): void {
    const contents: SlotContent[] = Array.from({ length: INVENTORY_ON_HAND }, () => ({}));
    for (const stack of this.stacks) {
      if (stack.slot < 0 || stack.slot >= INVENTORY_ON_HAND) continue;
      contents[stack.slot] = slotContentFor(stack);
    }
    /* 🙌 The arms win over the hand — T261. See {@link setCarrying}. */
    if (this.carried !== null && this.selected >= 0 && this.selected < INVENTORY_ON_HAND) {
      contents[this.selected] = this.carried;
    }
    this.setState({ contents, selected: this.selected });
  }

  /**
   * The controls line, in the cluster's own reserved row.
   *
   * 🔴 **This is where the old `.controls` strip went.** It sat at
   * `bottom: 12px`, which is exactly where the on-hand row goes — the collision
   * the design had already solved and that nothing here should rediscover.
   */
  /** 🎒 The last stacks the server sent, so the row can be rebuilt — T261. */
  private stacks: readonly Stack[] = [];
  /** Which slot the server says is chosen. */
  private selected = 0;
  /** 🙌 What is in the player's arms, or `null` — T261. */
  private carried: SlotContent | null = null;

  setHint(text: string | null): void {
    if (text === this.hintText) return;
    this.hintText = text;
    this.drawHint();
  }

  /**
   * 🐑 **What is under the crosshair, in the middle above the bars** — T286.
   *
   * 🎯 haugur, 2026-10-02: *"the whole Looking at text should always appear in
   * middle of sceen above health bars for players to notice."* ⚠️ Called from
   * the ~6 Hz readout, so it compares first like every setter here.
   */
  setAim(text: string | null, tone: AimTone): void {
    if (text === this.aimText && tone === this.aimTone) return;
    this.aimText = text;
    this.aimTone = tone;
    this.drawAim();
  }

  /** Fade the hint out once it has been read, or bring it back. */
  fadeHint(faded: boolean): void {
    this.hint.classList.toggle('faded', faded);
  }

  /**
   * Re-read the element's own size and redraw if it moved.
   *
   * ⚠️ **`clientWidth` is 0 until the element has been laid out**, and a scene
   * built at zero puts the cluster off the top of the screen. Nothing is drawn
   * until there is a real size.
   */
  private measure(): void {
    const w = this.svg.clientWidth;
    const h = this.svg.clientHeight;
    if (w === this.rawW && h === this.rawH) return;
    if (w <= 0 || h <= 0) return;
    this.rawW = w;
    this.rawH = h;
    /* 🔍 T170 — the same zoom the open pack uses, and it has to be the same or
       the hotbar and the panel that opens above it stop being one drawing.
       ⚠️ Nothing here hit-tests, so unlike `pack.ts` there is no pointer to
       correct: this element is `pointer-events: none` and the world takes every
       click that lands on it. */
    this.scale = hudScale(w, h);
    this.width = w / this.scale;
    this.height = h / this.scale;
    this.svg.setAttribute('viewBox', `0 0 ${String(this.width)} ${String(this.height)}`);
    this.drawBody();
    this.drawHint();
    this.drawAim();
  }

  private scene(): ReturnType<typeof makeScene> {
    return makeScene({ SW: this.width, SH: this.height });
  }

  private drawBody(): void {
    if (this.width <= 0) return;
    /* `controls: null` — the hint is drawn separately so it can fade on its own. */
    replace(this.body, this.scene().cluster(VARIANT, { ...this.state, controls: null }));
  }

  private drawHint(): void {
    if (this.width <= 0) return;
    replace(this.hint, this.hintText === null ? '' : this.scene().hintLine(this.hintText));
  }

  private drawAim(): void {
    if (this.width <= 0) return;
    replace(
      this.aim,
      this.aimText === null ? '' : this.scene().aimLine(this.aimText, this.aimTone),
    );
  }
}

/**
 * Put a fragment of SVG markup inside an element.
 *
 * ⚠️ **Parsed, not assigned to `innerHTML`.** Everything drawn here is generated
 * by our own code out of numbers and our own translated strings, so nothing
 * hostile can reach it today — but `hud.ts` builds its rows from DOM nodes for
 * the same reason, and the moment an item's name comes from somewhere else this
 * is the line that would have had to change. It is cheaper to never need to.
 */
function replace(target: SVGGElement, markup: string): void {
  const parsed = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`,
    'image/svg+xml',
  );
  const root = parsed.documentElement;
  /* A parse error comes back as an XML document whose root is `parsererror`,
     not as an exception. Silently drawing nothing is better than throwing in
     the render loop, but it must be visible to whoever is looking. */
  if (root.nodeName === 'parsererror' || root.getElementsByTagName('parsererror').length > 0) {
    console.error('HUD: could not parse the drawing', root.textContent);
    return;
  }
  target.replaceChildren(...Array.from(root.childNodes).map((n) => document.importNode(n, true)));
}

/**
 * Everything one slot needs to draw itself: the picture, the name and the count.
 *
 * 🔴 **Exported, because the open pack draws the same slots** (T152). A second
 * copy would be a second place for an item's picture to be looked up, and this
 * module already exists because the HUD once carried its own list of item names
 * and one of them had quietly drifted from `items.json`.
 *
 * 🔴 **The name is the ICELANDIC one**, because that is what the rest of the
 * game already shows — the readout says *Standing on Móberg*, not *Moberg*. 🎯
 * haugur, 2026-08-18: *"we need to add text under the pic to tell the player
 * what it is."*
 *
 * ⚠️ **An item with no picture still shows its name and count.** A slot that
 * looks empty while holding something is the quiet wrongness this project keeps
 * paying for; `hud-icons.test.ts` is what stops it lasting.
 */
export function slotContentFor(stack: Stack): SlotContent {
  const def = itemById(itemIdForKey(stack.itemKey) ?? -1);
  const label = def?.nameIs ?? stack.itemKey;
  const blockKey = def?.placesBlock;
  const base: SlotContent = { item: stack.itemKey, count: stack.count, label, wear: wearOf(stack) };

  /**
   * 🪓 **A photographed item wins, and it is asked FIRST.**
   *
   * 🎯 **haugur, 2026-09-02:** *"i would really like our tools in game to look
   * like that please."*
   *
   * 🔴 **This is the PACK picture only.** `helditem.ts` asks `picture()` with
   * `drawnOnly`, so what you HOLD is still built from the drawn icon's paths —
   * `SVGLoader` cannot make triangles out of a bitmap, and a texture in the hand
   * is what turned dev and staging black on 2026-09-01.
   */
  const photo = itemIconUri(stack.itemKey);
  if (photo !== undefined) return { ...base, photoUri: photo };

  if (blockKey === undefined) return base;
  const uri = blockIconUri(blockKey);
  if (uri !== undefined) return { ...base, iconUri: uri };
  /* No tile at all — fall back to the block's flat researched colour. */
  const colour = blockById(blockIdForKey(blockKey) ?? -1)?.colour;
  return colour === undefined ? base : { ...base, colour };
}

/**
 * 🔨 **How worn this tool is, 0 to 1** — or `undefined` for everything that
 * cannot wear out, which is nearly everything.
 *
 * 🎯 **haugur, 2026-09-09**, having chosen the bar: the wear had been real and
 * invisible since D104. **An axe lasts 100 blocks and a stone one 300**, and
 * until now the only way to find out how close yours was to the end was for it
 * to disappear.
 *
 * 🔴 **THE DIVISION HAPPENS HERE AND NOWHERE ELSE.** `hud-parts.ts` may not
 * import `@lofi/shared` — the four design generators resolve it directly — so
 * it cannot know any item's durability and takes a fraction instead. **This is
 * the one place that turns "37 blocks broken" into "a third gone."**
 *
 * ⚠️ **A tool that is past its own limit clamps to 1 rather than overflowing.**
 * `isWornOut` says a tool at or beyond its durability is finished, and the
 * server destroys it — but a client that is a frame behind must draw an empty
 * bar, never a negative one.
 */
function wearOf(stack: Stack): number | undefined {
  const max = durabilityOf(stack.itemKey);
  if (max === undefined || max <= 0) return undefined;
  const used = stack.used ?? 0;
  if (used <= 0) return undefined;
  return Math.min(1, used / max);
}

/**
 * Are these two on-hand rows the same?
 *
 * 🔴 **Compared by VALUE, not by identity.** `setInventory` builds a fresh
 * array every time the server speaks, so an identity check would redraw all ten
 * slots on every `inventory` message — including the ones that arrive when only
 * the selected slot changed.
 *
 * 🔴 **`wear` IS PART OF THE COMPARISON, AND LEAVING IT OUT WAS A REAL BUG.**
 * A tool wearing out changes **neither its item nor its count** — a tool has
 * `stack: 1` and stays one — so with only those two fields the row was judged
 * unchanged and **the bar would have frozen at whatever it was the last time
 * the slot itself changed.** ⚠️ **Nothing would have failed**: the number was
 * right, the drawing was right, and the screen simply never redrew. **It would
 * have been found by haugur chopping wood and watching a bar not move.**
 *
 * 👉 **The rule this is an instance of: anything a slot DRAWS belongs here.**
 * The list is the drawing's inputs, not the item's identity.
 */
function sameContents(a: readonly SlotContent[], b: readonly SlotContent[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((slot, i) => {
    /* eslint-disable-next-line security/detect-object-injection --
       `i` is the index `every` is walking, over an array whose length was just
       compared to b's. ⚠️ Read ONCE into a local rather than indexed three
       times: three suppressions on one line is a comment nobody reads. */
    const other = b[i];
    return slot.item === other?.item && slot.count === other?.count && slot.wear === other?.wear;
  });
}
